import { tEn, tZh } from '../i18n';
import type { Card, Position } from '../types';
import { attachColorTooltip } from './color-tooltip';
import { escapeHtml } from './escape';

const LIGHT_CATS = new Set(['white']);
const DARK_CATS = new Set(['black']);

const CATEGORY_ZH: Record<string, string> = {
  red: '红',
  orange: '橙',
  yellow: '黄',
  green: '绿',
  blue: '蓝',
  purple: '紫',
  pink: '粉',
  white: '白',
  black: '黑',
};

export function createReadingBlock(
  card: Card,
  position: Position,
  showAISkeleton: boolean,
  useImages: boolean,
): HTMLElement {
  const block = document.createElement('div');
  block.className = 'reading-block';
  block.dataset.cardId = String(card.id);

  const faceContent = useImages ? cardImageHtml(card) : cardGlyphHtml(card);
  const faceAttrs = useImages ? 'class="rb-card-face rb-card-face--img"' : glyphFaceAttrs(card);
  block.innerHTML = `
    <div class="rb-card">
      <div ${faceAttrs}>${faceContent}</div>
    </div>
    <div class="rb-content">
      <div class="rb-position">
        <span lang="en">${escapeHtml(position.label_en)}</span>
        <span lang="zh">${escapeHtml(position.label_zh)}</span>
      </div>
      <div class="rb-name">
        <span lang="en"><em>${escapeHtml(card.name_en)}</em></span>
        <span lang="zh">${escapeHtml(card.name_zh)}</span>
      </div>
      <div class="rb-sub">
        <span lang="en">${escapeHtml(card.category.toUpperCase())} · ${escapeHtml(card.keywords_en)}</span>
        <span lang="zh">${escapeHtml(CATEGORY_ZH[card.category] ?? card.category.toUpperCase())} · ${escapeHtml(card.keywords_zh)}</span>
      </div>
      <div class="rb-guidebook">
        <span lang="en">${escapeHtml(card.meaning_en)}</span>
        <span lang="zh">${escapeHtml(card.meaning_zh)}</span>
      </div>
      ${showAISkeleton ? aiSkeletonHtml() : ''}
    </div>`;
  requestAnimationFrame(() => block.classList.add('in'));

  const face = block.querySelector<HTMLElement>('.rb-card-face');
  if (face) attachColorTooltip(face, card.category);

  return block;
}

export function setAIText(block: HTMLElement, textEn: string, textZh: string): void {
  const el = block.querySelector<HTMLElement>('.rb-ai');
  if (!el) return;
  el.classList.remove('skeleton');
  el.innerHTML = `<span lang="en"><em>${escapeHtml(textEn)}</em></span><span lang="zh">${escapeHtml(textZh)}</span>`;
}

export function markAIFailed(block: HTMLElement): void {
  const el = block.querySelector<HTMLElement>('.rb-ai');
  if (!el) return;
  el.classList.remove('skeleton');
  el.innerHTML = `<span style="opacity:0.55; font-size:13px;">
    <span lang="en">${tEn('reading.ai_failed')}</span>
    <span lang="zh">${tZh('reading.ai_failed')}</span>
  </span>`;
}

// Snapshot the runtime flag for one reading. Callers persist this on the
// reading state so a mid-reading toggle change doesn't produce a clarifier
// rendered in a different mode than the original cards.
//
// Default ON: a missing key means "use real images". The user has to
// explicitly opt out by storing '0', which is what the settings toggle
// writes when unchecked.
export function readUseCardImagesPref(): boolean {
  try {
    return localStorage.getItem('useCardImages') !== '0';
  } catch {
    return true;
  }
}

function cardImageHtml(card: Card): string {
  // Filename derivation is the source of truth — every file in
  // /public/cards/ must match this `${id}_${slug}.webp` shape exactly.
  // encodeURIComponent on the slug keeps the URL safe if a future card
  // name introduces apostrophes or other punctuation that survive the
  // whitespace-collapse step (today all 45 names are single ASCII words,
  // so encode is a no-op).
  const slug = card.name_en.toLowerCase().replace(/\s+/g, '-');
  const src = `/cards/${String(card.id).padStart(2, '0')}_${encodeURIComponent(slug)}.webp`;
  return `<img class="rb-card-img" src="${src}" alt="${escapeHtml(card.name_en)}" width="90" height="140" decoding="async" />`;
}

function glyphColors(card: Card): { glyphColor: string; safeColor: string; safeBorder: string } {
  const isLight = LIGHT_CATS.has(card.category);
  const isDark = DARK_CATS.has(card.category);
  const glyphColor = isLight ? '#2a2a2a' : '#ffffff';
  const borderColor = isDark ? '#3a3a3a' : 'rgba(255,255,255,0.3)';
  return {
    glyphColor,
    safeColor: sanitizeColor(card.color),
    safeBorder: sanitizeColor(borderColor),
  };
}

function glyphFaceAttrs(card: Card): string {
  const { safeColor, safeBorder } = glyphColors(card);
  return `class="rb-card-face" style="background:${safeColor};border-color:${safeBorder};"`;
}

function cardGlyphHtml(card: Card): string {
  const { glyphColor } = glyphColors(card);
  return `<div class="rb-card-glyph" style="color:${glyphColor}">${escapeHtml(card.glyph)}</div>
    <div class="rb-card-name" style="color:${glyphColor}">
      <span lang="en">${escapeHtml(card.name_en)}</span>
      <span lang="zh">${escapeHtml(card.name_zh)}</span>
    </div>`;
}

// Allow only CSS color tokens we actually use: hex, named colors, rgb/rgba.
function sanitizeColor(c: string): string {
  return /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]+|rgba?\([\d.,\s%]+\))$/.test(c) ? c : 'transparent';
}

function aiSkeletonHtml(): string {
  return `
    <div class="rb-divider">◇</div>
    <div class="rb-ai-label">
      <span lang="en">${tEn('reading.for_question')}</span>
      <span lang="zh">${tZh('reading.for_question')}</span>
    </div>
    <div class="rb-ai skeleton">
      <span lang="en">${tEn('reading.skeleton')}</span>
      <span lang="zh">${tZh('reading.skeleton')}</span>
    </div>`;
}
